import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Trash2,
  Save,
  Eye,
  Pencil,
  Download,
  Printer,
  CheckCircle2,
  Loader2,
  RotateCcw,
  Home,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import ConfirmActionDialog from "@/components/ConfirmActionDialog";
import ProofImageGallery from "@/components/ProofImageGallery";
import ProofStatusBadge from "@/components/ProofStatusBadge";
import ProofImageDropzone from "@/components/ProofImageDropzone";
import { PageHero, PageShell } from "@/components/PageShell";
import { InlineHint, PageLoadingState } from "@/components/PageStates";
import { getTodayInBusinessTz } from "@/lib/date";
import SearchableDropdown from "@/components/SearchableDropdown";
import { getFieldErrorClass, getFieldMessageClass } from "@/lib/form-ui";
import { handleArrowFieldKeyDown } from "@/lib/keyboardNav";
import { useNavigate } from "react-router-dom";
import { ROLE_KEPALA_RUANGAN } from "@/lib/roles";
import {
  TAX_THRESHOLD,
  hasSupplierNpwp,
  getItemSubtotalBeforeTax,
  calculateTaxBreakdown,
  getAppliedTaxDisplaySummary,
  getTaxBreakdownFromTransaksi,
} from "@/lib/barangMasukTax";
import { formatNumberId, formatRupiah, formatStockValue } from "@/lib/currency";
import {
  MAX_TRANSACTION_PROOF_IMAGES,
  MAX_TRANSACTION_PROOF_IMAGE_BYTES,
  PROOF_IMAGE_ACCEPT,
  buildProofImageSrc,
  createLocalProofImageEntries,
  isLikelyProofImageFile,
  getTransactionProofImageUrls,
} from "@/lib/proofImages";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BARANG_MASUK_DRAFT_KEY_PREFIX = "barang-masuk-draft";
const HISTORY_PAGE_SIZE = 10;

const formatThousandsInput = (value) => {
  const digitsOnly = String(value ?? "").replace(/\D/g, "");
  if (!digitsOnly) {
    return "";
  }
  return Number(digitsOnly).toLocaleString("id-ID");
};

const parseThousandsInput = (value) => {
  const digitsOnly = String(value ?? "").replace(/\D/g, "");
  if (!digitsOnly) {
    return NaN;
  }
  return Number(digitsOnly);
};

const getBarangUnits = (barang) => {
  const units = Object.keys(barang?.konversi_satuan || {});
  if (barang?.satuan && !units.includes(String(barang.satuan).toLowerCase())) {
    units.push(String(barang.satuan).toLowerCase());
  }
  return units;
};

const getFilenameFromDisposition = (disposition, fallbackName) => {
  const match = String(disposition || "").match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (!match?.[1]) {
    return fallbackName;
  }
  const raw = match[1].replace(/['"]/g, "").trim();
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

const getNotaFilenameByTanggal = (tanggal) => {
  if (!tanggal) {
    return "Nota.pdf";
  }

  const tanggalObj = new Date(tanggal);
  if (Number.isNaN(tanggalObj.getTime())) {
    return "Nota.pdf";
  }

  const tanggalLabel = tanggalObj
    .toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
    .toLowerCase();

  return `Nota (${tanggalLabel}).pdf`;
};

const extractAxiosBlobErrorMessage = async (error, fallbackMessage) => {
  const rawData = error?.response?.data;
  if (rawData && typeof rawData === "object" && !(rawData instanceof Blob)) {
    if (typeof rawData.detail === "string" && rawData.detail.trim()) {
      return rawData.detail;
    }
    if (typeof rawData.message === "string" && rawData.message.trim()) {
      return rawData.message;
    }
  }

  if (rawData instanceof Blob) {
    try {
      const text = await rawData.text();
      if (text) {
        try {
          const parsed = JSON.parse(text);
          if (typeof parsed?.detail === "string" && parsed.detail.trim()) {
            return parsed.detail;
          }
          if (typeof parsed?.message === "string" && parsed.message.trim()) {
            return parsed.message;
          }
        } catch {
          const compactText = text.trim();
          if (compactText) {
            return compactText;
          }
        }
      }
    } catch {
      // no-op
    }
  }

  const status = error?.response?.status;
  if (status === 404) {
    return "Endpoint nota belum tersedia di backend. Restart backend lalu coba lagi.";
  }
  return status ? `${fallbackMessage} (HTTP ${status})` : fallbackMessage;
};

const createProofGalleryItems = ({ existingUrls = [], newEntries = [], onRemoveExisting, onRemoveNew }) => [
  ...existingUrls.map((url, index) => ({
    id: `existing-${url}-${index}`,
    src: buildProofImageSrc(url),
    href: buildProofImageSrc(url),
    alt: `Foto bukti ${index + 1}`,
    label: `Foto ${index + 1}`,
    onRemove:
      typeof onRemoveExisting === "function" ? () => onRemoveExisting(index) : undefined,
  })),
  ...newEntries.map((entry, index) => ({
    id: entry.id,
    src: entry.previewUrl,
    href: entry.previewUrl,
    alt: `Foto bukti baru ${index + 1}`,
    label: `Baru ${index + 1}`,
    onRemove: typeof onRemoveNew === "function" ? () => onRemoveNew(entry.id) : undefined,
  })),
];

const SpjBadge = ({ spjStatus, className = "" }) => {
  const isSpj = Boolean(spjStatus);
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        isSpj
          ? "border-emerald-200 bg-emerald-100 text-emerald-800"
          : "border-slate-200 bg-slate-100 text-slate-600",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {isSpj ? <CheckCircle2 size={12} /> : null}
      {isSpj ? "SPJ" : "Belum SPJ"}
    </span>
  );
};

const BarangMasukPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [barangs, setBarangs] = useState([]);
  const [transaksi, setTransaksi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedTransaksi, setSelectedTransaksi] = useState(null);
  const [newFotoBuktiEntries, setNewFotoBuktiEntries] = useState([]);
  const [existingFotoUrls, setExistingFotoUrls] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getTodayInBusinessTz());
  const [searchBarang, setSearchBarang] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [editingPembelianId, setEditingPembelianId] = useState(null);
  const [formData, setFormData] = useState({
    supplier_id: "",
    bukti_nota: "",
    keterangan: "",
    items: [],
  });
  const [newItem, setNewItem] = useState({
    barang_id: "",
    qty: "",
    satuan_input: "",
    harga_satuan: "",
  });
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [editingItemDraft, setEditingItemDraft] = useState({
    qty_input: "",
    satuan_input: "",
    harga_satuan: "",
  });
  const [draftReady, setDraftReady] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transaksiOffset, setTransaksiOffset] = useState(0);
  const [totalTransaksi, setTotalTransaksi] = useState(0);
  const [hasMoreTransaksi, setHasMoreTransaksi] = useState(false);
  const [loadingMoreTransaksi, setLoadingMoreTransaksi] = useState(false);
  const [historyDateDraft, setHistoryDateDraft] = useState({
    startDate: "",
    endDate: "",
  });
  const [historyDateFilter, setHistoryDateFilter] = useState({
    startDate: "",
    endDate: "",
  });
  const [historySupplierFilter, setHistorySupplierFilter] = useState("all");
  const [historySpjFilter, setHistorySpjFilter] = useState("all");
  const [historySearchTerm, setHistorySearchTerm] = useState("");
  const [historySort, setHistorySort] = useState("desc");
  const [downloadingNotaId, setDownloadingNotaId] = useState("");
  const [printingNotaId, setPrintingNotaId] = useState("");
  const [spjUpdatingId, setSpjUpdatingId] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [newItemErrors, setNewItemErrors] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    description: "",
    confirmText: "Ya",
    destructive: false,
    onConfirm: null,
  });
  const draftKey = `${BARANG_MASUK_DRAFT_KEY_PREFIX}:${user?.username || "guest"}`;
  const canManageHistory = user?.role === ROLE_KEPALA_RUANGAN;
  const [activeBarangMasukTab, setActiveBarangMasukTab] = useState("form");
  const barangInputRef = useRef(null);
  const formSectionRef = useRef(null);
  const qtyInputRef = useRef(null);
  const satuanSelectRef = useRef(null);
  const hargaInputRef = useRef(null);
  const addItemButtonRef = useRef(null);
  const editQtyInputRef = useRef(null);
  const editSatuanSelectRef = useRef(null);
  const editHargaInputRef = useRef(null);
  const editSaveButtonRef = useRef(null);
  const transaksiFetchInFlightRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const formDataRef = useRef(formData);

  useEffect(() => {
    if (!canManageHistory) {
      setActiveBarangMasukTab("form");
    }
  }, [canManageHistory]);

  const focusFieldNextFrame = useCallback((fieldRef) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fieldRef.current?.focus();
      });
    });
  }, []);

  const focusQtyAfterBarangSelect = useCallback(() => {
    barangInputRef.current?.blur();
    focusFieldNextFrame(qtyInputRef);
  }, [focusFieldNextFrame]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    if (!user?.username) {
      setDraftReady(false);
      return;
    }

    setDraftReady(false);

    const rawDraft = localStorage.getItem(draftKey);
    if (!rawDraft) {
      setDraftReady(true);
      return;
    }

    try {
      const parsedDraft = JSON.parse(rawDraft);
      const draftFormData = parsedDraft?.formData;

      if (typeof parsedDraft?.selectedDate === "string") {
        setSelectedDate(parsedDraft.selectedDate);
      }

      if (draftFormData && typeof draftFormData === "object") {
        const restoredItems = Array.isArray(draftFormData.items)
          ? draftFormData.items
              .filter(
                (item) =>
                  item &&
                  typeof item.barang_id === "string" &&
                  item.barang_id.trim() !== "" &&
                  Number.isFinite(Number(item.qty)) &&
                  Number.isFinite(Number(item.harga_satuan)) &&
                  Number.isFinite(Number(item.subtotal))
              )
              .map((item) => ({
                barang_id: item.barang_id,
                qty: Number(item.qty),
                qty_input: Number(item.qty_input ?? item.qty),
                satuan_input:
                  typeof item.satuan_input === "string" ? item.satuan_input : "",
                harga_satuan: Number(item.harga_satuan),
                subtotal: Number(item.subtotal),
              }))
          : [];

        setFormData({
          supplier_id:
            typeof draftFormData.supplier_id === "string"
              ? draftFormData.supplier_id
              : "",
          bukti_nota:
            typeof draftFormData.bukti_nota === "string"
              ? draftFormData.bukti_nota
              : "",
          keterangan:
            typeof draftFormData.keterangan === "string"
              ? draftFormData.keterangan
              : "",
          items: restoredItems,
        });
      }
    } catch {
      localStorage.removeItem(draftKey);
    } finally {
      setDraftReady(true);
    }
  }, [draftKey, user?.username]);

  useEffect(() => {
    if (!user?.username || !draftReady || editMode) {
      return;
    }

      const draftPayload = {
        selectedDate,
        formData: {
          supplier_id: formData.supplier_id,
          bukti_nota: formData.bukti_nota,
          keterangan: formData.keterangan,
          items: formData.items,
        },
      };

    localStorage.setItem(draftKey, JSON.stringify(draftPayload));
  }, [
    draftKey,
    user?.username,
    draftReady,
    editMode,
    selectedDate,
    formData.supplier_id,
    formData.bukti_nota,
    formData.keterangan,
    formData.items,
  ]);

  const removeExistingFotoAt = useCallback((indexToRemove) => {
    setExistingFotoUrls((prev) => prev.filter((_, index) => index !== indexToRemove));
  }, []);

  const removeNewFotoById = useCallback((fotoId) => {
    setNewFotoBuktiEntries((prev) => prev.filter((entry) => entry.id !== fotoId));
  }, []);

  const resetFotoSelection = useCallback(({ preserveExisting = false } = {}) => {
    setNewFotoBuktiEntries([]);
    if (!preserveExisting) {
      setExistingFotoUrls([]);
    }
  }, []);

  const currentFotoCount = existingFotoUrls.length + newFotoBuktiEntries.length;

  const fotoGalleryItems = useMemo(
    () =>
      createProofGalleryItems({
        existingUrls: existingFotoUrls,
        newEntries: newFotoBuktiEntries,
        onRemoveExisting: removeExistingFotoAt,
        onRemoveNew: removeNewFotoById,
      }),
    [existingFotoUrls, newFotoBuktiEntries, removeExistingFotoAt, removeNewFotoById]
  );

  const fetchTransaksi = useCallback(
    async ({
      append = false,
      baseOffset = 0,
      filterStartDate,
      filterEndDate,
      filterSupplierId,
      filterSpjStatus,
      filterSearchTerm = "",
      filterSort,
    } = {}) => {
      if (append && transaksiFetchInFlightRef.current) {
        return;
      }

      const nextOffset = append ? baseOffset : 0;
      const startDateParam =
        typeof filterStartDate === "string"
          ? filterStartDate
          : historyDateFilter.startDate;
      const endDateParam =
        typeof filterEndDate === "string"
          ? filterEndDate
          : historyDateFilter.endDate;
      const supplierIdParam =
        typeof filterSupplierId === "string"
          ? filterSupplierId
          : historySupplierFilter;
      const spjStatusParam =
        typeof filterSpjStatus === "string"
          ? filterSpjStatus
          : historySpjFilter;
      const searchTermParam =
        typeof filterSearchTerm === "string" ? filterSearchTerm : "";
      const sortParam =
        typeof filterSort === "string" ? filterSort : historySort;
      const spjStatusQuery =
        spjStatusParam === "spj"
          ? true
          : spjStatusParam === "belum_spj"
            ? false
            : undefined;

      transaksiFetchInFlightRef.current = true;
      setLoadingMoreTransaksi(true);
      try {
        const transaksiRes = await axios.get(`${API}/barang-masuk`, {
          params: {
            limit: HISTORY_PAGE_SIZE,
            offset: nextOffset,
            start_date: startDateParam || undefined,
            end_date: endDateParam || undefined,
            supplier_id: supplierIdParam && supplierIdParam !== "all" ? supplierIdParam : undefined,
            spj_status: spjStatusQuery,
            search: searchTermParam.trim() || undefined,
            sort: sortParam,
          },
        });
        const rows = transaksiRes.data || [];
        const totalFromHeader = Number(transaksiRes.headers?.["x-total-count"]);
        setTransaksi((prev) => {
          const mergedRows = append ? [...prev, ...rows] : rows;
          const seenIds = new Set();
          return mergedRows.filter((row) => {
            const transaksiId = row?.pembelian_id;
            if (!transaksiId) {
              return true;
            }
            if (seenIds.has(transaksiId)) {
              return false;
            }
            seenIds.add(transaksiId);
            return true;
          });
        });
        const updatedOffset = nextOffset + rows.length;
        setTransaksiOffset(updatedOffset);
        if (Number.isFinite(totalFromHeader)) {
          setTotalTransaksi(totalFromHeader);
          setHasMoreTransaksi(updatedOffset < totalFromHeader);
        } else {
          setHasMoreTransaksi(rows.length === HISTORY_PAGE_SIZE);
        }
    } catch (error) {
      toast.error("Gagal memuat riwayat barang masuk");
    } finally {
      transaksiFetchInFlightRef.current = false;
      setLoadingMoreTransaksi(false);
    }
  },
    [
      historyDateFilter.endDate,
      historyDateFilter.startDate,
      historySpjFilter,
      historySupplierFilter,
      historySort,
    ]
  );

  const fetchData = useCallback(async () => {
    try {
      const [suppliersRes, barangsRes] = await Promise.all([
        axios.get(`${API}/supplier`),
        axios.get(`${API}/barang`),
      ]);
      setSuppliers(
        (suppliersRes.data || []).map((supplier) => ({
          ...supplier,
          aktif: supplier.aktif !== false,
          kategori_ids: Array.isArray(supplier.kategori_ids)
            ? supplier.kategori_ids
            : [],
        }))
      );
      setBarangs(barangsRes.data.filter((b) => b.aktif));
      if (canManageHistory) {
        await fetchTransaksi({ append: false, baseOffset: 0, filterSearchTerm: "" });
      }
    } catch (error) {
      toast.error("Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, [canManageHistory, fetchTransaksi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApplyHistoryDateFilter = () => {
    if (
      historyDateDraft.startDate &&
      historyDateDraft.endDate &&
      historyDateDraft.startDate > historyDateDraft.endDate
    ) {
      toast.error("Tanggal mulai tidak boleh lebih besar dari tanggal akhir");
      return;
    }

    const nextFilter = {
      startDate: historyDateDraft.startDate,
      endDate: historyDateDraft.endDate,
    };

    setHistoryDateFilter(nextFilter);
    fetchTransaksi({
      append: false,
      baseOffset: 0,
      filterStartDate: nextFilter.startDate,
      filterEndDate: nextFilter.endDate,
      filterSupplierId: historySupplierFilter,
      filterSpjStatus: historySpjFilter,
      filterSearchTerm: historySearchTerm,
      filterSort: historySort,
    });
  };

  const handleResetHistoryDateFilter = () => {
    const emptyFilter = { startDate: "", endDate: "" };
    setHistoryDateDraft(emptyFilter);
    setHistoryDateFilter(emptyFilter);
    setHistorySupplierFilter("all");
    setHistorySpjFilter("all");
    setHistorySearchTerm("");
    setHistorySort("desc");
    fetchTransaksi({
      append: false,
      baseOffset: 0,
      filterStartDate: "",
      filterEndDate: "",
      filterSupplierId: "all",
      filterSpjStatus: "all",
      filterSearchTerm: "",
      filterSort: "desc",
    });
  };

  const handleHistorySupplierChange = (value) => {
    setHistorySupplierFilter(value);
    fetchTransaksi({
      append: false,
      baseOffset: 0,
      filterStartDate: historyDateFilter.startDate,
      filterEndDate: historyDateFilter.endDate,
      filterSupplierId: value,
      filterSpjStatus: historySpjFilter,
      filterSearchTerm: historySearchTerm,
      filterSort: historySort,
    });
  };

  const handleHistorySpjChange = (value) => {
    setHistorySpjFilter(value);
    fetchTransaksi({
      append: false,
      baseOffset: 0,
      filterStartDate: historyDateFilter.startDate,
      filterEndDate: historyDateFilter.endDate,
      filterSupplierId: historySupplierFilter,
      filterSpjStatus: value,
      filterSearchTerm: historySearchTerm,
      filterSort: historySort,
    });
  };

  const handleHistorySortChange = (value) => {
    setHistorySort(value);
    fetchTransaksi({
      append: false,
      baseOffset: 0,
      filterStartDate: historyDateFilter.startDate,
      filterEndDate: historyDateFilter.endDate,
      filterSupplierId: historySupplierFilter,
      filterSpjStatus: historySpjFilter,
      filterSearchTerm: historySearchTerm,
      filterSort: value,
    });
  };

  const isHistoryFilterActive = Boolean(
    historyDateFilter.startDate ||
      historyDateFilter.endDate ||
      historySupplierFilter !== "all" ||
      historySpjFilter !== "all" ||
      historySort !== "desc"
  );

  const handleAddItem = () => {
    const qty = parseFloat(newItem.qty);
    const harga = parseThousandsInput(newItem.harga_satuan);
    const nextFormErrors = {};
    const nextItemErrors = {};

    if (!formData.supplier_id) {
      nextFormErrors.supplier_id = "Pilih supplier terlebih dahulu agar daftar barang sesuai kategori supplier.";
    }

    const selectedSupplier = suppliers.find((s) => s.supplier_id === formData.supplier_id);
    const supplierKategoriIds = Array.isArray(selectedSupplier?.kategori_ids)
      ? selectedSupplier.kategori_ids
      : [];

    if (supplierKategoriIds.length === 0) {
      nextFormErrors.supplier_id = "Supplier belum memiliki kategori aktif. Atur di Master Supplier atau pilih supplier lain.";
    }

    const selectedBarang = barangs.find((b) => b.barang_id === newItem.barang_id);
    if (!selectedBarang || !supplierKategoriIds.includes(selectedBarang.kategori_id)) {
      nextItemErrors.barang_id = "Barang harus sesuai dengan kategori supplier yang dipilih.";
    }

    if (!newItem.barang_id) {
      nextItemErrors.barang_id = nextItemErrors.barang_id || "Pilih barang terlebih dahulu.";
    }
    if (!qty || qty <= 0) {
      nextItemErrors.qty = "Qty harus lebih dari 0.";
    }
    if (!harga || harga <= 0) {
      nextItemErrors.harga_satuan = "Harga satuan harus lebih dari 0.";
    }

    const satuanInput = newItem.satuan_input || selectedBarang?.satuan || "";
    if (!selectedBarang || !satuanInput) {
      nextItemErrors.satuan_input = "Pilih satuan item terlebih dahulu.";
    }

    if (Object.keys(nextFormErrors).length > 0 || Object.keys(nextItemErrors).length > 0) {
      setFormErrors((prev) => ({ ...prev, ...nextFormErrors }));
      setNewItemErrors(nextItemErrors);
      return;
    }

    const konversiMap = selectedBarang.konversi_satuan || {};
    const faktor = Number(konversiMap[satuanInput.toLowerCase()] ?? (satuanInput.toLowerCase() === String(selectedBarang.satuan).toLowerCase() ? 1 : NaN));
    if (!Number.isFinite(faktor) || faktor <= 0) {
      setNewItemErrors({ satuan_input: "Satuan item belum terkonfigurasi di master barang." });
      return;
    }

    const qtyKonversi = qty * faktor;
    const subtotal = qty * harga;
    const item = {
      barang_id: newItem.barang_id,
      qty: qtyKonversi,
      qty_input: qty,
      satuan_input: satuanInput,
      harga_satuan: harga,
      subtotal: subtotal,
    };

    setFormData({
      ...formData,
      items: [...formData.items, item],
    });
    setFormErrors((prev) => ({ ...prev, items: "", supplier_id: "" }));
    setNewItemErrors({});

    setNewItem({
      barang_id: "",
      qty: "",
      satuan_input: "",
      harga_satuan: "",
    });
    setSearchBarang("");
    requestAnimationFrame(() => {
      barangInputRef.current?.focus();
    });
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
    if (editingItemIndex === index) {
      handleCancelEditItem();
    }
  };

  const handleStartEditItem = (item, index) => {
    setEditingItemIndex(index);
    setEditingItemDraft({
      qty_input: String(item.qty_input ?? item.qty ?? ""),
      satuan_input: item.satuan_input || "",
      harga_satuan: formatThousandsInput(item.harga_satuan),
    });
    focusFieldNextFrame(editQtyInputRef);
  };

  const handleCancelEditItem = () => {
    setEditingItemIndex(null);
    setEditingItemDraft({ qty_input: "", satuan_input: "", harga_satuan: "" });
    focusFieldNextFrame(barangInputRef);
  };

  const handleSaveEditItem = (index) => {
    const currentItem = formData.items[index];
    const barang = barangs.find((b) => b.barang_id === currentItem?.barang_id);
    if (!barang) {
      toast.error("Barang tidak ditemukan");
      return;
    }

    const qtyInput = parseFloat(editingItemDraft.qty_input);
    const hargaSatuan = parseThousandsInput(editingItemDraft.harga_satuan);
    const satuanInput = editingItemDraft.satuan_input || barang.satuan || "";

    if (!qtyInput || qtyInput <= 0 || !hargaSatuan || hargaSatuan <= 0) {
      toast.error("Qty dan harga harus lebih dari 0");
      return;
    }

    const konversiMap = barang.konversi_satuan || {};
    const faktor = Number(
      konversiMap[satuanInput.toLowerCase()] ??
        (satuanInput.toLowerCase() === String(barang.satuan).toLowerCase() ? 1 : NaN)
    );
    if (!Number.isFinite(faktor) || faktor <= 0) {
      toast.error("Satuan item belum terkonfigurasi");
      return;
    }

    const qtyKonversi = qtyInput * faktor;
    const subtotal = qtyInput * hargaSatuan;
    const updatedItems = formData.items.map((item, i) =>
      i === index
        ? {
            ...item,
            qty: qtyKonversi,
            qty_input: qtyInput,
            satuan_input: satuanInput,
            harga_satuan: hargaSatuan,
            subtotal,
          }
        : item
    );

    setFormData({ ...formData, items: updatedItems });
    handleCancelEditItem();
  };

  const executeSubmit = async () => {
    if (isSubmitting || submitInFlightRef.current) {
      return;
    }
    if (formData.items.length === 0) {
      setFormErrors((prev) => ({ ...prev, items: "Tambahkan minimal 1 item sebelum menyimpan transaksi." }));
      return;
    }
    if (currentFotoCount > MAX_TRANSACTION_PROOF_IMAGES) {
      toast.error(`Maksimal ${MAX_TRANSACTION_PROOF_IMAGES} foto bukti per transaksi`);
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);
    const wasEditing = editMode;
    try {
      const uploadedFotoUrls = [];
      for (const fotoEntry of newFotoBuktiEntries) {
        const fotoFormData = new FormData();
        fotoFormData.append("file", fotoEntry.file);
        const uploadRes = await axios.post(`${API}/upload/barang-masuk`, fotoFormData);
        uploadedFotoUrls.push(uploadRes.data.url);
      }

      const fotoUrls = [...existingFotoUrls, ...uploadedFotoUrls];

      const submitData = new FormData();
      submitData.append("supplier_id", formData.supplier_id);
      submitData.append("items", JSON.stringify(formData.items));
      submitData.append("bukti_nota", formData.bukti_nota || "");
      submitData.append("keterangan", formData.keterangan || "");
      submitData.append("foto_bukti_list", JSON.stringify(fotoUrls));
      submitData.append("tanggal", selectedDate);

      if (editMode && editingPembelianId) {
        await axios.put(`${API}/barang-masuk/${editingPembelianId}`, submitData);
        toast.success("Transaksi barang masuk berhasil diupdate");
      } else {
        await axios.post(`${API}/barang-masuk`, submitData);
        toast.success("Barang masuk berhasil dicatat");
      }
      setFormData({
        supplier_id: "",
        bukti_nota: "",
        keterangan: "",
        items: [],
      });
      setEditMode(false);
      setEditingPembelianId(null);
      handleCancelEditItem();
      setSelectedDate(getTodayInBusinessTz());
      if (user?.username) {
        localStorage.removeItem(draftKey);
      }
      resetFotoSelection();
      setSubmitConfirmOpen(false);
      fetchData();
      setActiveBarangMasukTab(wasEditing && canManageHistory ? "riwayat" : "form");
      if (wasEditing && canManageHistory) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, behavior: "smooth" });
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal menyimpan data");
    } finally {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = {};
    if (!formData.supplier_id) {
      nextErrors.supplier_id = "Pilih supplier sebelum menyimpan transaksi.";
    }
    if (formData.items.length === 0) {
      nextErrors.items = "Tambahkan minimal 1 item sebelum menyimpan transaksi.";
    }
    setFormErrors((prev) => ({ ...prev, ...nextErrors }));
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    setSubmitConfirmOpen(true);
  };

  const handleFotoFilesSelected = async (files) => {
    const selectedFiles = Array.from(files || []);

    if (selectedFiles.length === 0) {
      return;
    }

    const imageFiles = selectedFiles.filter(isLikelyProofImageFile);
    if (imageFiles.length === 0) {
      toast.error("Semua file foto bukti harus berupa gambar");
      return;
    }

    if (imageFiles.length < selectedFiles.length) {
      toast.warning("Beberapa file diabaikan karena bukan gambar");
    }

    const validSizeFiles = imageFiles.filter(
      (file) => Number(file.size || 0) <= MAX_TRANSACTION_PROOF_IMAGE_BYTES
    );
    if (validSizeFiles.length === 0) {
      toast.error("Ukuran setiap foto maksimal 5MB");
      return;
    }

    if (validSizeFiles.length < imageFiles.length) {
      toast.warning("Beberapa foto diabaikan karena melebihi 5MB");
    }

    const remainingSlots = MAX_TRANSACTION_PROOF_IMAGES - currentFotoCount;
    if (remainingSlots <= 0) {
      toast.error(`Maksimal ${MAX_TRANSACTION_PROOF_IMAGES} foto bukti per transaksi`);
      return;
    }

    const nextFiles = validSizeFiles.slice(0, remainingSlots);
    if (validSizeFiles.length > remainingSlots) {
      toast.warning(`Hanya ${remainingSlots} foto yang ditambahkan karena batas maksimal ${MAX_TRANSACTION_PROOF_IMAGES} foto.`);
    }

    try {
      const nextEntries = await createLocalProofImageEntries(nextFiles, "barang-masuk");
      setNewFotoBuktiEntries((prev) => [...prev, ...nextEntries]);
    } catch {
      toast.error("Gagal menyiapkan preview foto bukti");
    }
  };

  const getBarangName = useCallback((barangId) => {
    const barang = barangs.find((b) => b.barang_id === barangId);
    return barang ? barang.nama_barang : "-";
  }, [barangs]);

  const getBarangSatuan = (barangId) => {
    const barang = barangs.find((b) => b.barang_id === barangId);
    return barang ? barang.satuan : "";
  };

  const getItemQtyWithSatuan = (item) => {
    const qtyDisplay = item?.qty_input ?? item?.qty ?? 0;
    const satuanDisplay = item?.satuan_input || getBarangSatuan(item?.barang_id) || "-";
    return `${qtyDisplay} ${satuanDisplay}`;
  };

  const getSupplierName = useCallback((supplierId) => {
    const supplier = suppliers.find((s) => s.supplier_id === supplierId);
    return supplier ? supplier.nama_supplier : "-";
  }, [suppliers]);

  const getSupplierById = (supplierId) =>
    suppliers.find((supplier) => supplier.supplier_id === supplierId);

  const historySupplierOptions = useMemo(
    () =>
      [...suppliers]
        .sort((a, b) => String(a.nama_supplier || "").localeCompare(String(b.nama_supplier || ""), "id"))
        .map((supplier) => ({
          value: supplier.supplier_id,
          label: `${supplier.nama_supplier}${supplier.aktif === false ? " (Nonaktif)" : ""}`,
        })),
    [suppliers]
  );

  const getTransaksiTaxBreakdown = (trx) => {
    const supplier = getSupplierById(trx?.supplier_id);
    const supplierHasNpwp =
      typeof trx?.supplier_memiliki_npwp === "boolean"
        ? trx.supplier_memiliki_npwp
        : hasSupplierNpwp(supplier);

    return {
      supplierHasNpwp,
      ...getTaxBreakdownFromTransaksi(trx, supplierHasNpwp),
    };
  };

  const filteredHistoryTransaksi = useMemo(() => {
    const keyword = historySearchTerm.trim().toLowerCase();

    return transaksi.filter((entry) => {
      if (!keyword) {
        return true;
      }

      const searchableParts = [
        getSupplierName(entry?.supplier_id),
        entry?.petugas,
        entry?.bukti_nota,
        entry?.keterangan,
        ...(entry?.items || []).map((item) => getBarangName(item?.barang_id)),
      ];

      return searchableParts
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(keyword);
    });
  }, [getBarangName, getSupplierName, historySearchTerm, transaksi]);

  const isHistorySearchActive = Boolean(isHistoryFilterActive || historySearchTerm.trim());

  const fetchNotaPdfResponse = async (pembelianId) =>
    axios.get(`${API}/barang-masuk/${pembelianId}/nota-pdf?t=${Date.now()}`, {
      responseType: "blob",
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

  const triggerDownloadFromBlobResponse = (response, fallbackName) => {
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: "application/pdf" });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    const filename = getFilenameFromDisposition(
      response.headers?.["content-disposition"],
      fallbackName
    );
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const handleDownloadNota = async (transaksiItem) => {
    const pembelianId = transaksiItem?.pembelian_id;
    if (!pembelianId) {
      return;
    }

    const fallbackFilename = getNotaFilenameByTanggal(transaksiItem?.tanggal);

    setDownloadingNotaId(pembelianId);
    try {
      const response = await fetchNotaPdfResponse(pembelianId);
      triggerDownloadFromBlobResponse(response, fallbackFilename);
      toast.success("Nota berhasil didownload");
    } catch (error) {
      const errorMessage = await extractAxiosBlobErrorMessage(error, "Gagal download nota");
      toast.error(errorMessage);
    } finally {
      setDownloadingNotaId("");
    }
  };

  const handlePrintNota = async (pembelianId) => {
    if (!pembelianId) {
      return;
    }
    setPrintingNotaId(pembelianId);
    try {
      const response = await fetchNotaPdfResponse(pembelianId);
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
          iframe.parentNode?.removeChild(iframe);
        }, 1500);
      };
      iframe.src = blobUrl;
      document.body.appendChild(iframe);
      toast.success("Dialog print nota dibuka");
    } catch (error) {
      const errorMessage = await extractAxiosBlobErrorMessage(error, "Gagal print nota");
      toast.error(errorMessage);
    } finally {
      setPrintingNotaId("");
    }
  };

  const handleViewDetail = (t) => {
    setSelectedTransaksi(t);
    setDetailDialog(true);
  };

  const handleToggleSpjStatus = async (transaksiItem) => {
    const pembelianId = transaksiItem?.pembelian_id;
    if (!canManageHistory || !pembelianId || spjUpdatingId === pembelianId) {
      return;
    }

    setSpjUpdatingId(pembelianId);
    try {
      const response = await axios.patch(`${API}/barang-masuk/${pembelianId}/spj`);
      const updatedTransaksi = response.data;
      setTransaksi((prev) =>
        prev.map((row) => (row.pembelian_id === pembelianId ? updatedTransaksi : row))
      );
      setSelectedTransaksi((prev) =>
        prev?.pembelian_id === pembelianId ? updatedTransaksi : prev
      );
      toast.success(updatedTransaksi.spj_status ? "Transaksi ditandai SPJ" : "Status SPJ dibatalkan");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Gagal memperbarui status SPJ");
    } finally {
      setSpjUpdatingId("");
    }
  };

  const handleEditTransaksi = (t) => {
    setEditMode(true);
    setEditingPembelianId(t.pembelian_id);
    setSelectedDate(String(t.tanggal).slice(0, 10));
    setActiveBarangMasukTab("form");
    setFormData({
      supplier_id: t.supplier_id || "",
      bukti_nota: t.bukti_nota || "",
      keterangan: t.keterangan || "",
      items: (t.items || []).map((item) => ({
        barang_id: item.barang_id,
        qty: Number(item.qty),
        qty_input: Number(item.qty_input ?? item.qty),
        satuan_input: item.satuan_input || "",
        harga_satuan: Number(item.harga_satuan_input ?? item.harga_satuan),
        subtotal: Number(item.subtotal_input ?? item.subtotal),
      })),
    });
    setExistingFotoUrls(getTransactionProofImageUrls(t));
    setNewFotoBuktiEntries([]);
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
      formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleDeleteTransaksi = async (pembelianId) => {
    setConfirmDialog({
      open: true,
      title: "Konfirmasi Hapus Transaksi",
      description: "Apakah Anda yakin ingin menghapus transaksi ini? Tindakan ini tidak dapat dibatalkan.",
      confirmText: "Ya, Hapus",
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/barang-masuk/${pembelianId}`);
          toast.success("Transaksi barang masuk berhasil dihapus");
          if (editMode && editingPembelianId === pembelianId) {
            handleCancelEdit();
          }
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Gagal menghapus transaksi");
        }
      },
    });
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingPembelianId(null);
    if (canManageHistory) {
      setActiveBarangMasukTab("riwayat");
    }
    setFormData({
      supplier_id: "",
      bukti_nota: "",
      keterangan: "",
      items: [],
    });
    setSelectedDate(getTodayInBusinessTz());
    resetFotoSelection();
    if (canManageHistory) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
  };

  const getFilteredItemsBySupplierKategori = useCallback(
    (items, kategoriIds) =>
      (items || []).filter((item) => {
        const barang = barangs.find((b) => b.barang_id === item.barang_id);
        return barang && kategoriIds.includes(barang.kategori_id);
      }),
    [barangs]
  );

  const applySupplierSelection = useCallback(({
    currentSupplierId,
    nextSupplierId,
    nextItems,
    removedItemsCount,
  }) => {
    setFormData((prev) => {
      if (prev.supplier_id !== currentSupplierId) {
        return prev;
      }
      return {
        ...prev,
        supplier_id: nextSupplierId,
        items: nextItems,
      };
    });

    if (removedItemsCount > 0) {
      toast.warning(
        removedItemsCount === 1
          ? "1 item dihapus karena tidak sesuai kategori supplier"
          : `${removedItemsCount} item dihapus karena tidak sesuai kategori supplier`
      );
    }

    setNewItem({ barang_id: "", qty: "", satuan_input: "", harga_satuan: "" });
    setSearchBarang("");
  }, []);

  const handleSupplierChange = useCallback(
    (nextSupplierId) => {
      const currentFormData = formDataRef.current;
      const currentSupplierId = currentFormData?.supplier_id || "";

      if (!nextSupplierId || nextSupplierId === currentSupplierId) {
        return;
      }

      const nextSupplier = suppliers.find((s) => s.supplier_id === nextSupplierId);
      const nextKategoriIds = Array.isArray(nextSupplier?.kategori_ids)
        ? nextSupplier.kategori_ids
        : [];

      const filteredItems = getFilteredItemsBySupplierKategori(
        currentFormData?.items || [],
        nextKategoriIds
      );
      const removedItemsCount = (currentFormData?.items || []).length - filteredItems.length;

      const applyChange = () => {
        setFormErrors((prev) => ({ ...prev, supplier_id: "" }));
        applySupplierSelection({
          currentSupplierId,
          nextSupplierId,
          nextItems: filteredItems,
          removedItemsCount,
        });
      };

      if (removedItemsCount > 0) {
        setConfirmDialog({
          open: true,
          title: "Konfirmasi Ganti Supplier",
          description:
            removedItemsCount === 1
              ? "1 item akan dihapus karena tidak sesuai kategori supplier baru. Lanjutkan?"
              : `${removedItemsCount} item akan dihapus karena tidak sesuai kategori supplier baru. Lanjutkan?`,
          confirmText: "Ya, Ganti Supplier",
          destructive: false,
          onConfirm: async () => {
            applyChange();
          },
        });
        return;
      }

      applyChange();
    },
    [applySupplierSelection, getFilteredItemsBySupplierKategori, suppliers]
  );

  const selectedSupplier = suppliers.find((s) => s.supplier_id === formData.supplier_id);
  const activeSuppliers = suppliers.filter((supplier) => supplier.aktif !== false);
  const supplierOptions =
    selectedSupplier && selectedSupplier.aktif === false
      ? [
          selectedSupplier,
          ...activeSuppliers.filter(
            (supplier) => supplier.supplier_id !== selectedSupplier.supplier_id
          ),
        ]
      : activeSuppliers;
  const supplierKategoriIds = Array.isArray(selectedSupplier?.kategori_ids)
    ? selectedSupplier.kategori_ids
    : [];
  const supplierHasKategori = !formData.supplier_id || supplierKategoriIds.length > 0;

  const filteredBarangs = barangs.filter((b) => {
    if (!b.aktif) {
      return false;
    }
    if (!formData.supplier_id) {
      return false;
    }
    return supplierKategoriIds.includes(b.kategori_id);
  });

  const supplierHasNpwpOnForm = hasSupplierNpwp(selectedSupplier);
  const formSubtotalSebelumPajak = formData.items.reduce(
    (sum, item) => sum + getItemSubtotalBeforeTax(item),
    0
  );
  const formTaxBreakdown = calculateTaxBreakdown(
    formSubtotalSebelumPajak,
    supplierHasNpwpOnForm
  );
  const formDisplaySummary = getAppliedTaxDisplaySummary(formData.items, formTaxBreakdown);
  const formDisplayItems = formDisplaySummary.displayItems;
  const selectedTransaksiTaxBreakdown = selectedTransaksi
    ? getTransaksiTaxBreakdown(selectedTransaksi)
    : null;
  const selectedTransaksiDisplaySummary = selectedTransaksi
    ? {
        subtotalSebelumPajak: Number(selectedTransaksi.subtotal_sebelum_pajak ?? 0),
        nilaiPajak: Number(selectedTransaksi.nilai_pajak ?? 0),
        totalBelanja: Number(selectedTransaksi.total_belanja ?? 0),
        tarifPajakPersen: Number(selectedTransaksi.tarif_pajak_persen ?? 0),
      }
    : {
        subtotalSebelumPajak: 0,
        nilaiPajak: 0,
        totalBelanja: 0,
        tarifPajakPersen: 0,
      };
  const selectedTransaksiDisplayItems = selectedTransaksi?.items || [];
  const selectedTransaksiFotoGalleryItems = useMemo(
    () =>
      getTransactionProofImageUrls(selectedTransaksi).map((url, index) => {
        const imageSrc = buildProofImageSrc(url);
        return {
          id: `${selectedTransaksi?.pembelian_id || "masuk"}-${index}`,
          src: imageSrc,
          href: imageSrc,
          alt: `Foto bukti transaksi ${index + 1}`,
          label: `Foto ${index + 1}`,
        };
      }),
    [selectedTransaksi]
  );

  if (loading) {
    return <PageLoadingState label="Memuat transaksi barang masuk" description="Menyiapkan supplier, barang, dan data terbaru." />;
  }

  return (
    <PageShell data-testid="barang-masuk-page">
      <PageHero
        tone="emerald"
        eyebrow="Operasional Penerimaan"
        title="Transaksi Barang Masuk Dapur"
        description="Catat pembelian harian dari supplier dengan detail item, nota, dan bukti transaksi dalam kerangka halaman yang lebih konsisten."
        actions={(
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/dashboard")}
            className="shrink-0"
          >
            <Home size={16} />
            Dashboard
          </Button>
        )}
      />

      <div className="space-y-6">
        <Tabs
          value={activeBarangMasukTab}
          onValueChange={setActiveBarangMasukTab}
          className="space-y-6"
        >
          {canManageHistory ? (
            <TabsList className="mx-auto grid w-full max-w-xl grid-cols-2 border border-emerald-200 bg-white/95">
              <TabsTrigger
                value="form"
                data-testid="tab-barang-masuk-form"
                className="text-slate-700 hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md"
              >
                Form Barang Masuk
              </TabsTrigger>
              <TabsTrigger
                value="riwayat"
                data-testid="tab-barang-masuk-riwayat"
                className="text-slate-700 hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 data-[state=active]:bg-emerald-600 data-[state=active]:text-white data-[state=active]:shadow-md"
              >
                Riwayat Barang Masuk
              </TabsTrigger>
            </TabsList>
          ) : null}

          <TabsContent value="form" className="space-y-6 mt-0">
            <div ref={formSectionRef} className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-6 shadow-sm">
          <h2 className="font-manrope font-extrabold text-2xl md:text-[1.75rem] mb-6 text-emerald-900">
            {editMode ? "Edit Transaksi Barang Masuk" : "Form Penerimaan Barang"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label>Supplier</Label>
              <Select
                value={formData.supplier_id}
                onValueChange={handleSupplierChange}
              >
                <SelectTrigger
                  data-testid="select-supplier"
                  className={getFieldErrorClass(formErrors.supplier_id)}
                  aria-invalid={Boolean(formErrors.supplier_id)}
                >
                  <SelectValue placeholder="Pilih supplier" />
                </SelectTrigger>
                <SelectContent>
                  {supplierOptions.map((supplier) => (
                    <SelectItem
                      key={supplier.supplier_id}
                      value={supplier.supplier_id}
                      disabled={!supplier.kategori_ids || supplier.kategori_ids.length === 0}
                    >
                      {supplier.nama_supplier}
                      {supplier.aktif === false ? " (Nonaktif)" : ""}
                      {!supplier.kategori_ids || supplier.kategori_ids.length === 0
                        ? " (Belum ada kategori)"
                        : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.supplier_id && selectedSupplier?.aktif === false && (
                <p className="text-sm text-amber-700">
                  Supplier ini sudah nonaktif. Anda masih bisa mengedit transaksi lama,
                  namun supplier nonaktif tidak ditampilkan untuk transaksi baru.
                </p>
              )}
              {formData.supplier_id && !supplierHasKategori && (
                <p className="text-sm text-destructive">
                  Supplier belum memiliki kategori. Pilih supplier lain atau atur di Master Supplier.
                </p>
              )}
              {formErrors.supplier_id ? (
                <p className={getFieldMessageClass(true)}>{formErrors.supplier_id}</p>
              ) : (
                <p className={getFieldMessageClass()}>
                  Supplier menentukan daftar barang yang bisa dipilih pada transaksi ini.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Tanggal Transaksi</Label>
              <Input
                data-testid="input-tanggal-masuk"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={getTodayInBusinessTz()}
              />
            </div>

            <div className="space-y-2">
              <Label>No. Bukti/Nota (Opsional)</Label>
              <Input
                data-testid="input-bukti-nota"
                value={formData.bukti_nota}
                onChange={(e) =>
                  setFormData({ ...formData, bukti_nota: e.target.value })
                }
                placeholder="Nomor nota pembelian"
              />
              <p className={getFieldMessageClass()}>
                Opsional, tetapi nomor bukti membantu pencarian dan audit riwayat transaksi.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Catatan Transaksi (Opsional)</Label>
              <Textarea
                data-testid="input-keterangan-barang-masuk"
                value={formData.keterangan}
                onChange={(e) => setFormData({ ...formData, keterangan: e.target.value })}
                placeholder="Contoh: Barang dikirim sore, cek ulang jumlah dus."
                rows={3}
              />
              <p className={getFieldMessageClass()}>
                Catatan ini akan tampil di detail transaksi dan di nota barang masuk.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Foto Bukti Barang (Opsional)</Label>
              <ProofImageDropzone
                inputTestId="input-foto-bukti"
                accept={PROOF_IMAGE_ACCEPT}
                multiple
                tone="emerald"
                currentCount={currentFotoCount}
                maxCount={MAX_TRANSACTION_PROOF_IMAGES}
                onFilesSelected={handleFotoFilesSelected}
              />
              <p className={getFieldMessageClass()}>
                Upload sampai {MAX_TRANSACTION_PROOF_IMAGES} foto. Setiap file maksimal 5MB dan otomatis dikompres saat disimpan.
              </p>
              {currentFotoCount > 0 ? (
                <ProofImageGallery
                  title={`Foto Bukti (${currentFotoCount}/${MAX_TRANSACTION_PROOF_IMAGES})`}
                  hint="Klik gambar untuk membuka ukuran penuh. Anda bisa menghapus foto satu per satu sebelum menyimpan."
                  images={fotoGalleryItems}
                />
              ) : null}
            </div>

            <div className="border-t border-emerald-200/80 pt-6">
              <h3 className="font-manrope font-bold text-xl md:text-2xl mb-4">Tambah Item</h3>
              <InlineHint className="border-emerald-200/80 bg-white/70">
                Pilih barang sesuai supplier, lalu lengkapi qty, satuan, dan harga. Draft transaksi tetap tersimpan otomatis selama Anda belum menyimpan.
              </InlineHint>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Barang</Label>
                  <div className="space-y-2">
                    <SearchableDropdown
                      value={newItem.barang_id}
                      disabled={!formData.supplier_id || !supplierHasKategori}
                      searchValue={searchBarang}
                      onSearchValueChange={setSearchBarang}
                      onValueChange={(value) => {
                        const barang = filteredBarangs.find((b) => b.barang_id === value);
                        const hargaSatuan =
                          barang && Number(barang.harga_satuan) > 0
                            ? formatNumberId(barang.harga_satuan)
                            : "";
                        setNewItem({
                          ...newItem,
                          barang_id: value,
                          satuan_input: barang?.satuan || "",
                          harga_satuan: hargaSatuan,
                        });
                        setNewItemErrors((prev) => ({ ...prev, barang_id: "", satuan_input: "", harga_satuan: "" }));
                        setSearchBarang("");
                        focusQtyAfterBarangSelect();
                      }}
                      error={newItemErrors.barang_id}
                      helperText={
                        !formData.supplier_id
                          ? "Pilih supplier lebih dulu untuk memfilter barang yang tersedia."
                          : filteredBarangs.length > 0
                            ? "Ketik nama barang untuk mempercepat pencarian."
                            : "Belum ada barang yang cocok dengan kategori supplier ini."
                      }
                      selectedLabel="Barang terpilih"
                      selectedPlaceholder={
                        !formData.supplier_id ? "Pilih supplier dulu" : "Pilih barang"
                      }
                      emptyText="Tidak ada barang yang cocok"
                      inputTestId="search-barang"
                      listTestId="select-barang"
                      inputRef={barangInputRef}
                      items={filteredBarangs.map((barang) => ({
                        value: barang.barang_id,
                        label: `${barang.nama_barang} (${barang.satuan}) - Stok: ${formatStockValue(barang.stok_current)}`,
                        searchText: barang.nama_barang,
                      }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Qty</Label>
                    <Input
                      ref={qtyInputRef}
                      data-testid="input-qty"
                      type="number"
                      placeholder="Masukkan qty"
                      value={newItem.qty}
                        onChange={(e) =>
                          {
                            setNewItem({
                              ...newItem,
                              qty: e.target.value,
                            });
                            setNewItemErrors((prev) => ({ ...prev, qty: "" }));
                          }
                        }
                      onKeyDown={(event) =>
                        handleArrowFieldKeyDown(event, {
                          onEnter: () => satuanSelectRef.current?.focus(),
                          onLeft: () => barangInputRef.current?.focus(),
                          onRight: () => satuanSelectRef.current?.focus(),
                        })
                      }
                        className={getFieldErrorClass(newItemErrors.qty)}
                        aria-invalid={Boolean(newItemErrors.qty)}
                      />
                      {newItemErrors.qty ? <p className={getFieldMessageClass(true)}>{newItemErrors.qty}</p> : null}
                    </div>
                    <div className="space-y-2">
                      <Label>Satuan Qty</Label>
                      <Select
                      value={newItem.satuan_input}
                      onValueChange={(value) =>
                        {
                          setNewItem({ ...newItem, satuan_input: value });
                          setNewItemErrors((prev) => ({ ...prev, satuan_input: "" }));
                        }
                      }
                      disabled={!newItem.barang_id}
                    >
                      <SelectTrigger
                        ref={satuanSelectRef}
                        className={getFieldErrorClass(newItemErrors.satuan_input)}
                        aria-invalid={Boolean(newItemErrors.satuan_input)}
                        onKeyDown={(event) =>
                          handleArrowFieldKeyDown(event, {
                            onLeft: () => qtyInputRef.current?.focus(),
                            onRight: () => hargaInputRef.current?.focus(),
                          })
                        }
                      >
                        <SelectValue placeholder="Pilih satuan" />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          const selectedBarang = barangs.find((b) => b.barang_id === newItem.barang_id);
                          return getBarangUnits(selectedBarang).map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                    {newItemErrors.satuan_input ? <p className={getFieldMessageClass(true)}>{newItemErrors.satuan_input}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <Label>Harga Satuan</Label>
                    <Input
                      ref={hargaInputRef}
                      data-testid="input-harga"
                      type="text"
                      inputMode="numeric"
                      placeholder="-"
                      value={newItem.harga_satuan}
                      onChange={(e) =>
                        {
                          setNewItem({
                            ...newItem,
                            harga_satuan: formatThousandsInput(e.target.value),
                          });
                          setNewItemErrors((prev) => ({ ...prev, harga_satuan: "" }));
                        }
                      }
                      onKeyDown={(event) =>
                        handleArrowFieldKeyDown(event, {
                          onEnter: handleAddItem,
                          onLeft: () => satuanSelectRef.current?.focus(),
                          onRight: () => addItemButtonRef.current?.focus(),
                        })
                      }
                      className={getFieldErrorClass(newItemErrors.harga_satuan)}
                      aria-invalid={Boolean(newItemErrors.harga_satuan)}
                    />
                    {newItemErrors.harga_satuan ? (
                      <p className={getFieldMessageClass(true)}>{newItemErrors.harga_satuan}</p>
                    ) : (
                      <p className={getFieldMessageClass()}>Harga otomatis diambil dari master barang dan masih bisa disesuaikan.</p>
                    )}
                  </div>
                </div>

                <Button
                  ref={addItemButtonRef}
                  data-testid="add-item-button"
                  type="button"
                  variant="outline"
                  onKeyDown={(event) =>
                    handleArrowFieldKeyDown(event, {
                      onLeft: () => hargaInputRef.current?.focus(),
                    })
                  }
                  onClick={handleAddItem}
                  className="w-full"
                  disabled={!formData.supplier_id || !supplierHasKategori}
                >
                  <Plus size={16} className="mr-2" />
                  Tambah Item
                </Button>
                {formData.supplier_id && filteredBarangs.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center">
                    Tidak ada barang yang sesuai kategori supplier.
                  </p>
                )}
                {formErrors.items ? <p className={getFieldMessageClass(true)}>{formErrors.items}</p> : null}
              </div>
            </div>

            {formData.items.length > 0 && (
              <div className="border-t border-emerald-200/80 pt-6">
                <h3 className="font-manrope font-bold text-xl md:text-2xl mb-4">Daftar Item</h3>
                <div className="space-y-2 mb-4">
                  {formData.items.map((item, index) => (
                    <div
                      key={index}
                      data-testid={`item-${index}`}
                      className={`flex items-center justify-between p-3 border rounded-md transition-colors ${
                        editingItemIndex === index
                          ? "border-primary bg-primary/5"
                          : ""
                      }`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-xs font-manrope font-bold text-emerald-800">
                            {index + 1}
                          </span>
                          <p className="font-medium">
                            {getBarangName(item.barang_id)}
                          </p>
                        </div>
                        {editingItemIndex === index && (
                          <p className="text-xs text-primary font-medium mt-1">Sedang diedit</p>
                        )}
                        {editingItemIndex === index ? (
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                            <Input
                              ref={editQtyInputRef}
                              type="number"
                              min="0"
                              step="any"
                              value={editingItemDraft.qty_input}
                              onChange={(e) =>
                                setEditingItemDraft({
                                  ...editingItemDraft,
                                  qty_input: e.target.value,
                                })
                              }
                              placeholder="Qty"
                              onKeyDown={(event) =>
                                handleArrowFieldKeyDown(event, {
                                  onEnter: () => editSatuanSelectRef.current?.focus(),
                                  onRight: () => editSatuanSelectRef.current?.focus(),
                                })
                              }
                            />
                              <Select
                              value={editingItemDraft.satuan_input}
                              onValueChange={(value) =>
                                {
                                  setEditingItemDraft({
                                    ...editingItemDraft,
                                    satuan_input: value,
                                  });
                                }
                              }
                            >
                              <SelectTrigger
                                ref={editSatuanSelectRef}
                                onKeyDown={(event) =>
                                  handleArrowFieldKeyDown(event, {
                                    onLeft: () => editQtyInputRef.current?.focus(),
                                    onRight: () => editHargaInputRef.current?.focus(),
                                  })
                                }
                              >
                                <SelectValue placeholder="Satuan" />
                              </SelectTrigger>
                              <SelectContent>
                                {getBarangUnits(
                                  barangs.find((b) => b.barang_id === item.barang_id)
                                ).map((unit) => (
                                  <SelectItem key={unit} value={unit}>
                                    {unit}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                              <Input
                                ref={editHargaInputRef}
                                type="text"
                                inputMode="numeric"
                                value={editingItemDraft.harga_satuan}
                              onChange={(e) =>
                                setEditingItemDraft({
                                  ...editingItemDraft,
                                  harga_satuan: formatThousandsInput(e.target.value),
                                })
                              }
                              placeholder="Harga"
                              onKeyDown={(event) =>
                                handleArrowFieldKeyDown(event, {
                                  onEnter: () => handleSaveEditItem(index),
                                  onLeft: () => editSatuanSelectRef.current?.focus(),
                                  onRight: () => editSaveButtonRef.current?.focus(),
                                })
                              }
                            />
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {item.qty} x {formatRupiah(item.harga_satuan)} = {" "}
                            {formatRupiah(item.subtotal)}
                            {item.qty_input && item.satuan_input
                              ? ` (${item.qty_input} ${item.satuan_input})`
                              : ""}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {editingItemIndex === index ? (
                          <>
                            <Button
                              ref={editSaveButtonRef}
                              type="button"
                              size="sm"
                              onKeyDown={(event) =>
                                handleArrowFieldKeyDown(event, {
                                  onLeft: () => editHargaInputRef.current?.focus(),
                                })
                              }
                              onClick={() => handleSaveEditItem(index)}
                            >
                              Simpan
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={handleCancelEditItem}>
                              Batal
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleStartEditItem(item, index)}
                          >
                            <Pencil size={16} className="mr-1" />
                            Ubah
                          </Button>
                        )}
                        <Button
                          data-testid={`remove-item-${index}`}
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <Trash2 size={16} className="text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-muted/50 p-4 rounded-md space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Subtotal Sebelum Pajak</span>
                    <span className="font-medium">
                      {formatRupiah(formDisplaySummary.subtotalSebelumPajak)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">
                      Pajak ({formTaxBreakdown.tarifPajakPersen.toLocaleString("id-ID")}%)
                    </span>
                    <span className="font-medium">
                      {formatRupiah(formDisplaySummary.nilaiPajak)}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between items-center">
                    <span className="font-manrope font-bold">Total Belanja</span>
                    <span className="font-manrope font-bold text-xl text-primary">
                      {formatRupiah(formDisplaySummary.totalBelanja)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formTaxBreakdown.tarifPajakPersen > 0
                      ? `Pajak diterapkan otomatis karena subtotal > ${formatRupiah(TAX_THRESHOLD)}.`
                      : `Tidak kena pajak karena subtotal <= ${formatRupiah(TAX_THRESHOLD)}.`}
                  </p>
                </div>
              </div>
            )}

            <Button
              data-testid="submit-barang-masuk"
              type="submit"
              className="w-full"
              disabled={!formData.supplier_id || formData.items.length === 0 || isSubmitting}
            >
              <Save size={16} className="mr-2" />
              {editMode ? "Update Transaksi" : "Simpan Transaksi"}
            </Button>
            {editMode && (
              <Button type="button" variant="outline" className="w-full" onClick={handleCancelEdit}>
                Batal Edit
              </Button>
            )}
          </form>
            </div>
          </TabsContent>

          {canManageHistory ? (
            <TabsContent value="riwayat" className="space-y-6 mt-0">
              <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-6 shadow-sm">
          <h2 className="font-manrope font-extrabold text-2xl md:text-[1.75rem] mb-6 text-slate-900">
            Riwayat Barang Masuk
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4 mb-3">
            <div>
              <Label>Tanggal mulai</Label>
              <Input
                type="date"
                value={historyDateDraft.startDate}
                onChange={(e) =>
                  setHistoryDateDraft((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <Label>Tanggal akhir</Label>
              <Input
                type="date"
                value={historyDateDraft.endDate}
                onChange={(e) =>
                  setHistoryDateDraft((prev) => ({
                    ...prev,
                    endDate: e.target.value,
                  }))
                }
              />
            </div>
            <div>
              <Label>Supplier</Label>
              <Select value={historySupplierFilter} onValueChange={handleHistorySupplierChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Semua supplier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua supplier</SelectItem>
                  {historySupplierOptions.map((supplier) => (
                    <SelectItem key={supplier.value} value={supplier.value}>
                      {supplier.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status SPJ</Label>
              <Select value={historySpjFilter} onValueChange={handleHistorySpjChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Semua status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua status</SelectItem>
                  <SelectItem value="spj">SPJ</SelectItem>
                  <SelectItem value="belum_spj">Belum SPJ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mb-3">
            <Label>Cari riwayat</Label>
            <div className="relative">
              <Input
                type="text"
                value={historySearchTerm}
                onChange={(e) => setHistorySearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleApplyHistoryDateFilter();
                  }
                }}
                placeholder="Cari supplier, petugas, nota, atau nama barang"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-slate-100 transition-colors"
                onClick={() => handleHistorySortChange(historySort === "desc" ? "asc" : "desc")}
                aria-label={historySort === "desc" ? "Urutan: Terbaru dulu — klik untuk Terlama dulu" : "Urutan: Terlama dulu — klik untuk Terbaru dulu"}
                title={historySort === "desc" ? "Terbaru dulu (DESC) — klik untuk ASC" : "Terlama dulu (ASC) — klik untuk DESC"}
              >
                {historySort === "desc" ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button type="button" size="sm" variant="outline" onClick={handleApplyHistoryDateFilter}>
              Terapkan Filter
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleResetHistoryDateFilter}
              disabled={
                !isHistorySearchActive &&
                !historyDateDraft.startDate &&
                !historyDateDraft.endDate &&
                !historySearchTerm &&
                historySupplierFilter === "all" &&
                historySpjFilter === "all"
              }
            >
              Reset Semua
            </Button>
          </div>
          {transaksi.length > 0 && (
              <p className="text-sm text-muted-foreground mb-3">
                Menampilkan {filteredHistoryTransaksi.length} hasil dari {transaksi.length} data dimuat{totalTransaksi > 0 ? ` dari total ${totalTransaksi}` : ""}
              </p>
          )}
          {isHistorySearchActive && transaksi.length > 0 && filteredHistoryTransaksi.length === 0 && hasMoreTransaksi && (
            <p className="mb-3 text-sm text-muted-foreground">
              Belum ada hasil pada data yang sudah dimuat. Coba ubah kata kunci atau muat data tambahan.
            </p>
          )}
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {filteredHistoryTransaksi.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                {transaksi.length === 0
                  ? isHistoryFilterActive
                    ? "Tidak ada transaksi pada filter ini"
                    : "Belum ada transaksi"
                  : "Tidak ada hasil yang cocok dengan pencarian"}
              </p>
            ) : (
              filteredHistoryTransaksi.map((t) => (
                <div
                  key={t.pembelian_id}
                  data-testid={`transaksi-${t.pembelian_id}`}
                  className="border rounded-md p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-manrope font-semibold">
                        {getSupplierName(t.supplier_id)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(t.tanggal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        {t.status}
                      </span>
                      <SpjBadge spjStatus={t.spj_status} />
                      <ProofStatusBadge hasProof={getTransactionProofImageUrls(t).length > 0} />
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {canManageHistory && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditTransaksi(t)}
                        >
                          <Pencil size={16} className="mr-1" />
                          Edit
                        </Button>
                      )}
                      {canManageHistory && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteTransaksi(t.pembelian_id)}
                        >
                          <Trash2 size={16} className="mr-1" />
                          Hapus
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDetail(t)}
                      >
                        <Eye size={16} className="mr-1" />
                        Detail
                      </Button>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1 mb-2">
                  {t.items.map((item, idx) => (
                    <p key={idx} className="text-sm">
                      {idx + 1}. {getBarangName(item.barang_id)} - {getItemQtyWithSatuan(item)}
                    </p>
                  ))}
                  {t.keterangan && (
                    <div className="rounded-md border border-dashed border-emerald-200 bg-emerald-50/60 p-3 text-sm text-slate-700">
                      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Catatan</p>
                      <p className="mt-1 whitespace-pre-line">{t.keterangan}</p>
                    </div>
                  )}
                </div>
                  <div className="border-t pt-2 mt-2">
                    <p className="font-manrope font-bold text-primary">
                      Total: {formatRupiah(Number(t.total_belanja ?? 0))}
                    </p>
                  </div>
                </div>
              ))
            )}
            {transaksi.length > 0 && (
              <div className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={!hasMoreTransaksi || loadingMoreTransaksi}
                  onClick={() =>
                    fetchTransaksi({
                      append: true,
                      baseOffset: transaksiOffset,
                      filterSearchTerm: historySearchTerm,
                    })
                  }
                >
                  {loadingMoreTransaksi
                    ? "Memuat..."
                    : hasMoreTransaksi
                      ? "Muat Lebih Banyak"
                      : "Semua data sudah ditampilkan"}
                </Button>
              </div>
            )}
          </div>
          </div>
            </TabsContent>
          ) : null}
        </Tabs>
      </div>

      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Barang Masuk</DialogTitle>
          </DialogHeader>
          {selectedTransaksi && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Supplier</p>
                  <p className="font-manrope font-semibold">
                    {getSupplierName(selectedTransaksi.supplier_id)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tanggal</p>
                  <p className="font-medium">
                    {new Date(selectedTransaksi.tanggal).toLocaleDateString(
                      "id-ID",
                      {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Petugas</p>
                  <p className="font-medium">{selectedTransaksi.petugas}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {selectedTransaksi.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status SPJ</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <SpjBadge spjStatus={selectedTransaksi.spj_status} />
                    <ProofStatusBadge hasProof={selectedTransaksiFotoGalleryItems.length > 0} />
                  </div>
                </div>
                {selectedTransaksiTaxBreakdown && (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Status NPWP Supplier</p>
                      <p className="font-medium">
                        {selectedTransaksiTaxBreakdown.supplierHasNpwp
                          ? "Memiliki NPWP"
                          : "Tidak memiliki NPWP"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Tarif Pajak</p>
                      <p className="font-medium">
                        {selectedTransaksiDisplaySummary.tarifPajakPersen.toLocaleString("id-ID")}%
                      </p>
                    </div>
                  </>
                )}
                {selectedTransaksi.bukti_nota && (
                  <div>
                    <p className="text-sm text-muted-foreground">No. Bukti/Nota</p>
                    <p className="font-medium">{selectedTransaksi.bukti_nota}</p>
                  </div>
                )}
                {selectedTransaksi.keterangan && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-muted-foreground">Catatan</p>
                    <p className="font-medium whitespace-pre-line">{selectedTransaksi.keterangan}</p>
                  </div>
                )}
              </div>

              {canManageHistory ? (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant={selectedTransaksi.spj_status ? "destructive" : "default"}
                    onClick={() => handleToggleSpjStatus(selectedTransaksi)}
                    disabled={spjUpdatingId === selectedTransaksi.pembelian_id}
                  >
                    {spjUpdatingId === selectedTransaksi.pembelian_id ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Memperbarui...
                      </>
                    ) : selectedTransaksi.spj_status ? (
                      <>
                        <RotateCcw size={16} className="mr-2" />
                        Batal SPJ
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={16} className="mr-2" />
                        Tandai SPJ
                      </>
                    )}
                  </Button>
                </div>
              ) : null}

              {selectedTransaksiFotoGalleryItems.length > 0 ? (
                <ProofImageGallery
                  title={`Foto Bukti (${selectedTransaksiFotoGalleryItems.length})`}
                  hint="Klik gambar untuk membuka ukuran penuh. Semua bukti ditampilkan dengan ukuran thumbnail yang konsisten."
                  images={selectedTransaksiFotoGalleryItems}
                />
              ) : null}

              <div>
                <p className="text-sm font-manrope font-semibold mb-2">
                  Daftar Item ({selectedTransaksi.items.length})
                </p>
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-3 py-2 text-center w-14">No</th>
                        <th className="px-3 py-2 text-left">Barang</th>
                        <th className="px-3 py-2 text-center">Qty</th>
                        <th className="px-3 py-2 text-right">Harga</th>
                        <th className="px-3 py-2 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTransaksiDisplayItems.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 text-center font-medium">{idx + 1}</td>
                          <td className="px-3 py-2">
                            {getBarangName(item.barang_id)}
                          </td>
                          <td className="px-3 py-2 text-center">{getItemQtyWithSatuan(item)}</td>
                          <td className="px-3 py-2 text-right">
                            {formatRupiah(Number(item.harga_satuan ?? 0))}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {formatRupiah(Number(item.subtotal ?? 0))}
                          </td>
                        </tr>
                      ))}
                      {selectedTransaksiTaxBreakdown && (
                        <>
                          <tr className="border-t bg-muted/20">
                            <td colSpan="4" className="px-3 py-2 text-right font-medium">
                              SUBTOTAL SEBELUM PAJAK:
                            </td>
                            <td className="px-3 py-2 text-right font-medium">
                              {formatRupiah(selectedTransaksiDisplaySummary.subtotalSebelumPajak)}
                            </td>
                          </tr>
                          <tr className="border-t bg-muted/20">
                            <td colSpan="4" className="px-3 py-2 text-right font-medium">
                              PAJAK ({selectedTransaksiDisplaySummary.tarifPajakPersen.toLocaleString("id-ID")}%)
                            </td>
                            <td className="px-3 py-2 text-right font-medium">
                              {formatRupiah(selectedTransaksiDisplaySummary.nilaiPajak)}
                            </td>
                          </tr>
                          <tr className="border-t font-manrope font-bold bg-muted/30">
                            <td colSpan="4" className="px-3 py-2 text-right">
                              TOTAL BELANJA:
                            </td>
                            <td className="px-3 py-2 text-right text-primary">
                              {formatRupiah(selectedTransaksiDisplaySummary.totalBelanja)}
                            </td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleDownloadNota(selectedTransaksi)}
                  disabled={
                    !selectedTransaksi.pembelian_id ||
                    downloadingNotaId === selectedTransaksi.pembelian_id
                  }
                >
                  <Download size={16} className="mr-2" />
                  {downloadingNotaId === selectedTransaksi.pembelian_id
                    ? "Mendownload..."
                    : "Download Nota"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handlePrintNota(selectedTransaksi.pembelian_id)}
                  disabled={
                    !selectedTransaksi.pembelian_id ||
                    printingNotaId === selectedTransaksi.pembelian_id
                  }
                >
                  <Printer size={16} className="mr-2" />
                  {printingNotaId === selectedTransaksi.pembelian_id
                    ? "Menyiapkan..."
                    : "Print Nota"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={submitConfirmOpen} onOpenChange={setSubmitConfirmOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editMode ? "Konfirmasi Perbarui Barang Masuk" : "Konfirmasi Simpan Barang Masuk"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className={getFieldMessageClass()}>
              Tinjau ringkasan {formData.items.length} item berikut sebelum transaksi barang masuk diproses.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <p className="text-muted-foreground">Tanggal</p>
                <p className="font-medium">{selectedDate}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Supplier</p>
                <p className="font-medium">{getSupplierName(formData.supplier_id)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Total Belanja</p>
                <p className="font-manrope font-bold text-primary">
                  {formatRupiah(formDisplaySummary.totalBelanja)}
                </p>
              </div>
            </div>
            <div className="rounded-md border bg-muted/30 p-3 space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-muted-foreground">Subtotal Sebelum Pajak</p>
                <p className="font-medium">
                  {formatRupiah(formDisplaySummary.subtotalSebelumPajak)}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-muted-foreground">
                  Pajak ({formTaxBreakdown.tarifPajakPersen.toLocaleString("id-ID")}%)
                </p>
                <p className="font-medium">{formatRupiah(formDisplaySummary.nilaiPajak)}</p>
              </div>
              <p className="text-xs text-muted-foreground pt-1">
                Status NPWP supplier: {supplierHasNpwpOnForm ? "Memiliki NPWP" : "Tidak memiliki NPWP"}
              </p>
            </div>
            {formData.bukti_nota && (
              <div>
                <p className="text-muted-foreground">No. Bukti/Nota</p>
                <p className="font-medium">{formData.bukti_nota}</p>
              </div>
            )}
            {formData.keterangan && (
              <div>
                <p className="text-muted-foreground">Catatan</p>
                <p className="font-medium whitespace-pre-line">{formData.keterangan}</p>
              </div>
            )}
            {currentFotoCount > 0 ? (
              <ProofImageGallery
                title={`Preview Foto Bukti (${currentFotoCount}/${MAX_TRANSACTION_PROOF_IMAGES})`}
                hint="Klik gambar untuk membuka ukuran penuh. Foto yang dihapus dari sini tidak akan ikut tersimpan."
                images={fotoGalleryItems}
              />
            ) : null}
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-center w-14">No</th>
                    <th className="px-3 py-2 text-left">Barang</th>
                    <th className="px-3 py-2 text-center">Qty Input</th>
                    <th className="px-3 py-2 text-center">Qty Dasar</th>
                    <th className="px-3 py-2 text-right">Harga</th>
                    <th className="px-3 py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {formDisplayItems.map((item, idx) => (
                    <tr key={idx} className="border-t">
                      <td className="px-3 py-2 text-center font-medium">{idx + 1}</td>
                      <td className="px-3 py-2">{getBarangName(item.barang_id)}</td>
                      <td className="px-3 py-2 text-center">{item.qty_input ?? item.qty} {item.satuan_input || ""}</td>
                      <td className="px-3 py-2 text-center">{item.qty}</td>
                      <td className="px-3 py-2 text-right">
                        {formatRupiah(Number((item.display_harga_satuan ?? item.harga_satuan) || 0))}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {formatRupiah(Number((item.display_subtotal ?? item.subtotal) || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setSubmitConfirmOpen(false)} disabled={isSubmitting}>
                Batal
              </Button>
              <Button type="button" onClick={executeSubmit} disabled={isSubmitting}>
                Ya, {editMode ? "Perbarui" : "Simpan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmActionDialog
        open={confirmDialog.open}
        onOpenChange={(open) =>
          setConfirmDialog((prev) => ({ ...prev, open, onConfirm: open ? prev.onConfirm : null }))
        }
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText={confirmDialog.confirmText}
        destructive={confirmDialog.destructive}
        onConfirm={async () => {
          if (confirmDialog.onConfirm) {
            await confirmDialog.onConfirm();
          }
          setConfirmDialog((prev) => ({ ...prev, open: false, onConfirm: null }));
        }}
      />
    </PageShell>
  );
};

export default BarangMasukPage;
